import { Button } from '@/components/ui/Button';

const DEFAULT_OPTIONS = [25, 50, 100, 200];

interface PaginationFooterProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Page-size choices for the selector. Defaults to 25/50/100/200. */
  pageSizeOptions?: number[];
  /** Plural noun for the count label, e.g. "subscribers". */
  noun?: string;
}

/**
 * Always-visible pagination footer: total count, a per-page selector, and
 * Prev/Next. Shared across the subscriber list views.
 */
export function PaginationFooter({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_OPTIONS,
  noun = 'items',
}: PaginationFooterProps) {
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-[12px] text-or-slate-500">
      <div className="flex items-center gap-3">
        <span>
          {totalCount.toLocaleString()} {noun} · Page {page} of {lastPage}
        </span>
        <label className="flex items-center gap-1.5">
          Per page
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-lg border border-or-slate-200 bg-white px-2 text-[12px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(Math.min(lastPage, page + 1))}
          disabled={page >= lastPage}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
